import { FormErrorDisplay } from "@/components/form/form-error-display";
import TextFieldWithLabel from "@/components/form/hook-form/textfield-with-label";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { Checkbox } from "@/components/ui/checkbox";
import { Policy } from "@/pages/welcome-screen/policy";
import { Term } from "@/pages/welcome-screen/term";
import { AUTH_ACTIONS_LAYOUT_CLASS, AUTH_FORM_LAYOUT_CLASS } from "@/pages/auth-layout";
import { AUTH_CTA_CLASS, AUTH_LINK_CLASS, AuthScreenLayout } from "@/pages/auth-screen-layout";
import { APP_ROUTES } from "@/constants/app-routes";
import { useToast } from "@/hooks/ui/use-toast";
import { useRegister } from "@/hooks/use-register";
import { RegisterFormValues, RegisterSchema } from "@/schemas/register-schema";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { zodResolver } from "@hookform/resolvers/zod";
import { useIonRouter } from "@ionic/react";
import { Controller, useForm } from "react-hook-form";

const getSubmitButtonText = (isLoading: boolean) => {
  if (isLoading) {
    return "Sending code...";
  }
  return "Create an account";
};

// Credentials-only validity: gates the submit button on email + password so the user can still tap
// it without consenting — the missing-consent prompt is then surfaced on submit (see handleSubmit).
const CredentialsSchema = RegisterSchema.omit({ agreeToTerms: true });

const RegisterPage = () => {
  const navigation = useIonRouter();
  const { isLoading, error, isError, registerAsync } = useRegister();
  const { showError, showSuccess } = useToast();

  const initialValues: RegisterFormValues = {
    email: "",
    password: "",
    agreeToTerms: false,
  };

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    watch,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(RegisterSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const [email, password] = watch(["email", "password"]);
  const credentialsValid = CredentialsSchema.safeParse({ email, password }).success;

  async function handleRegister({ email, password }: RegisterFormValues) {
    try {
      await registerAsync({ email, password });
      const searchParams = new URLSearchParams({
        email,
      });
      showSuccess("Verification code sent to your email.", {
        duration: 3000,
      });
      navigation.push(`${APP_ROUTES.REGISTER_VERIFICATION}?${searchParams.toString()}`, "forward");
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("An unknown error occurred");
      }
    }
  }

  // Custom blur handler: only validate if field has content
  const handleEmailBlur = () => {
    const emailValue = getValues("email");
    if (emailValue && emailValue.trim() !== "") {
      trigger("email");
    }
  };

  const handlePasswordBlur = () => {
    const passwordValue = getValues("password");
    if (passwordValue && passwordValue.trim() !== "") {
      trigger("password");
    }
  };

  return (
    <AuthScreenLayout
      title="Let's get you set up."
      subtitle="A few details, then I'll make this yours."
    >
      <form className={AUTH_FORM_LAYOUT_CLASS} onSubmit={handleSubmit(handleRegister)}>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-[13px]">
            <TextFieldWithLabel
              control={control}
              name="email"
              label="Email"
              placeholder="Please enter your email"
              type="email"
              autocomplete="email"
              ariaLabel="Email"
              id="email"
              inputClassName="w-full"
              error={errors.email?.message}
              showErrorMessage={false}
              scrollToOnFocus
              onBlur={handleEmailBlur}
              theme="dark"
            />

            <TextFieldWithLabel
              control={control}
              name="password"
              label="Password"
              placeholder="Please enter your password"
              type="password"
              autocomplete="new-password"
              ariaLabel="Password"
              id="password"
              inputClassName="w-full"
              error={errors.password?.message}
              showErrorMessage={false}
              scrollToOnFocus
              onBlur={handlePasswordBlur}
              theme="dark"
            />
          </div>

          <Controller
            control={control}
            name="agreeToTerms"
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="agreeToTerms"
                    color="violet"
                    checked={field.value}
                    onChange={() => field.onChange(!field.value)}
                  />
                  <p className={cn(textVariants.bodySmall, "text-white")}>
                    I agree with the{" "}
                    <Term className={cn(AUTH_LINK_CLASS, "text-[14px] font-medium")} /> and{" "}
                    <Policy className={cn(AUTH_LINK_CLASS, "text-[14px] font-medium")} />
                  </p>
                </div>
                {fieldState.error && (
                  <p className={cn(textVariants.bodySmall, "text-text-input-error-default")}>
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />

          <div
            id="clerk-captcha"
            data-cl-theme="dark"
            data-cl-size="flexible"
            data-cl-language="en-UK"
          />
        </div>

        <div className={AUTH_ACTIONS_LAYOUT_CLASS}>
          <FilledButton
            disabled={!credentialsValid || isLoading}
            text={getSubmitButtonText(isLoading)}
            type="submit"
            color="white"
            textCase="normal"
            className={AUTH_CTA_CLASS}
          />

          <FormErrorDisplay
            formErrors={errors}
            apiError={isError ? error : null}
            fieldOrder={["email", "password"]}
          />

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => navigation.push(APP_ROUTES.LOGIN, "forward", "replace")}
              className={cn(
                textVariants.bodySmall,
                AUTH_LINK_CLASS,
                "mx-auto flex h-11 items-center justify-center gap-1 font-medium",
              )}
            >
              <span>Have an account?</span>
              <span className="underline">Sign In</span>
            </button>

            <p className={cn(textVariants.footnote, "px-2 text-center text-white")}>
              By tapping &ldquo;Sign in&rdquo; or &ldquo;Create an account&rdquo;, you agree to our{" "}
              <Term className={cn(AUTH_LINK_CLASS, "font-medium")} />. Learn how we process your
              data in our <Policy className={cn(AUTH_LINK_CLASS, "font-medium")} />.
            </p>
          </div>
        </div>
      </form>
    </AuthScreenLayout>
  );
};

export default RegisterPage;
